import { computed } from 'vue'
import { SWP_EMPLOYEE_ID } from '../../constants/swp.js'

/**
 * Composable for employee matching calculations
 * Provides match percentage and perfect match detection based on weekly availability
 */
export function useEmployeeMatching(weeklyAvailability, employees) {
  /**
   * Get relevant weeks filtered by preference or prospect date range
   */
  const getRelevantWeeks = (employee, preference, prospect) => {
    const weeks = weeklyAvailability.value[employee.simplicate_id]
    if (!weeks || weeks.length === 0) return []

    // Use preference dates if set, otherwise fall back to prospect dates
    const startDate = preference.startDate || prospect?.start_date?.split('T')[0]
    const endDate = preference.endDate || prospect?.expected_closing_date?.split('T')[0]

    if (!startDate || !endDate) return weeks

    const periodStart = new Date(startDate)
    const periodEnd = new Date(endDate)

    // Filter weeks to only include those that overlap with the period
    return weeks.filter(week => {
      const weekStart = new Date(week.week_start)
      const weekEnd = new Date(week.week_end)
      // Week overlaps with period if week starts before period ends AND week ends after period starts
      return weekStart <= periodEnd && weekEnd >= periodStart
    })
  }

  /**
   * Calculate match percentage for an employee based on weekly available vs required time
   * Uses week-by-week data to ensure accurate matching (not just totals)
   * Filters weeks based on preference dates (if set) or prospect dates
   */
  const calculateMatchPercentage = (employee, preference, prospect) => {
    if (!prospect || !preference.dpw || preference.dpw <= 0) return null

    // SWP is a placeholder — always 0% match
    if (employee.simplicate_id === SWP_EMPLOYEE_ID) return 0

    // Get weekly availability data for this employee, filtered by preference/prospect dates
    const relevantWeeks = getRelevantWeeks(employee, preference, prospect)
    if (relevantWeeks.length === 0) return null

    // Count how many weeks the employee can fulfill the required dpw
    let weeksCanFulfill = 0
    const totalWeeks = relevantWeeks.length

    for (const week of relevantWeeks) {
      const availableDays = week.available_days || 0
      // Employee can fulfill this week if they have at least the required days available
      if (availableDays >= preference.dpw) {
        weeksCanFulfill++
      }
    }

    if (totalWeeks === 0) return null

    // Calculate match: percentage of weeks where they can fulfill the required dpw
    const matchPercentage = Math.max(
      0,
      Math.min(100, Math.round((weeksCanFulfill / totalWeeks) * 100))
    )
    return matchPercentage
  }

  /**
   * Check if employee is a "perfect match" - they can fulfill the required dpw in most weeks
   * AND their available time closely matches the required time (would fill them up nicely)
   * Filters weeks based on preference dates (if set) or prospect dates
   */
  const isPerfectMatch = (employee, preference, prospect) => {
    if (!prospect || !preference.dpw || preference.dpw <= 0) return false

    // Get weekly availability data for this employee, filtered by preference/prospect dates
    const relevantWeeks = getRelevantWeeks(employee, preference, prospect)
    if (relevantWeeks.length === 0) return false

    // Count weeks where employee can fulfill AND would be filled up nicely (available close to required)
    let perfectWeeks = 0
    let canFulfillWeeks = 0

    for (const week of relevantWeeks) {
      const availableDays = week.available_days || 0

      if (availableDays >= preference.dpw) {
        canFulfillWeeks++
        // Perfect fit: available days is between 100% and 120% of required dpw
        // This means the project would fill them up almost exactly
        if (availableDays <= preference.dpw * 1.2) {
          perfectWeeks++
        }
      }
    }

    const totalWeeks = relevantWeeks.length
    if (totalWeeks === 0) return false

    // Perfect match: can fulfill at least 90% of weeks AND at least 50% of those are "perfect fit" weeks
    const canFulfillRatio = canFulfillWeeks / totalWeeks
    const perfectFitRatio = perfectWeeks / totalWeeks

    return canFulfillRatio >= 0.9 && perfectFitRatio >= 0.5
  }

  /**
   * Filter employees based on team and experience level selection, sorted by match %
   */
  const getFilteredEmployees = (preference, prospect) => {
    let filtered = employees.value

    // Filter by teams if selected (multiple)
    if (preference.teams && preference.teams.length > 0) {
      filtered = filtered.filter(emp => {
        return emp.teams?.some(team => preference.teams.includes(team.simplicate_id))
      })
    }

    // Filter by experience levels (functions) if selected (multiple)
    if (preference.experienceLevels && preference.experienceLevels.length > 0) {
      filtered = filtered.filter(emp => {
        return emp.function && preference.experienceLevels.includes(emp.function)
      })
    }

    // Sort by: 1) Perfect matches first, 2) Then by match percentage (100% first, then descending)
    return [...filtered].sort((a, b) => {
      const aIsPerfect = isPerfectMatch(a, preference, prospect)
      const bIsPerfect = isPerfectMatch(b, preference, prospect)

      // Perfect matches come first
      if (aIsPerfect && !bIsPerfect) return -1
      if (!aIsPerfect && bIsPerfect) return 1

      const aMatch = calculateMatchPercentage(a, preference, prospect) ?? -1
      const bMatch = calculateMatchPercentage(b, preference, prospect) ?? -1

      // Among non-perfect matches, prioritize those at exactly 100%
      if (aMatch === 100 && bMatch !== 100) return -1
      if (bMatch === 100 && aMatch !== 100) return 1

      // Then sort by match percentage descending
      return bMatch - aMatch
    })
  }

  /**
   * Get employee display info (name + team + function + match%)
   */
  const getEmployeeDisplayInfo = (employee, preference, prospect) => {
    const teamNames = employee.teams?.map(t => t.name).join(', ') || ''
    const functionName = employee.function || ''
    const matchPct = calculateMatchPercentage(employee, preference, prospect)
    const perfect = isPerfectMatch(employee, preference, prospect)

    let display = perfect ? '★ ' : ''
    display += employee.name
    if (teamNames || functionName) {
      const details = [teamNames, functionName].filter(Boolean).join(' - ')
      display += ` (${details})`
    }
    if (matchPct !== null) {
      display += ` [${matchPct}%]`
    }
    return display
  }

  /**
   * Get selected employee names for display (names only, no extra info)
   */
  const getSelectedEmployeeNames = (selectedIds, _preference, _prospect) => {
    return selectedIds
      .map(id => {
        const emp = employees.value.find(e => e.simplicate_id === id)
        if (!emp) return null
        return emp.name
      })
      .filter(Boolean)
  }

  /**
   * Get unique functions from employees
   */
  const uniqueFunctions = computed(() => {
    if (!employees.value) return []
    return [...new Set(employees.value.map(emp => emp.function).filter(Boolean))].sort()
  })

  return {
    calculateMatchPercentage,
    isPerfectMatch,
    getFilteredEmployees,
    getEmployeeDisplayInfo,
    getSelectedEmployeeNames,
    uniqueFunctions
  }
}
